import { useCounter, useDisclosure } from '@mantine/hooks';
import { IconDatabaseExport } from '@tabler/icons-react';
import { ColDef } from 'ag-grid-community';
import { useApi } from 'api/api-context';
import ActionsColumn from 'components/tables/data/system-export/columns/ActionsColumn';
import AuthorNameColumn from 'components/tables/data/system-export/columns/AuthorNameColumn';
import CreatedAtColumn from 'components/tables/data/system-export/columns/CreatedAtColumn';
import FileNameColumn from 'components/tables/data/system-export/columns/FileNameColumn';
import FileSizeColumn from 'components/tables/data/system-export/columns/FileSizeColumn';
import OrganizationNameColumn from 'components/tables/data/system-export/columns/OrganizationNameColumn';
import SystemExportIdColumn from 'components/tables/data/system-export/columns/SystemExportIdColumn';
import SystemExportModal from 'components/tables/data/system-export/SystemExportModal';
import FileDownloadLinkModal from 'components/tables/data/system-export/FileDownloadLinkModal';
import DataTable from 'components/tables/DataTable';
import DateFilter, { DateColumnFilter } from 'components/tables/filters/DateFilter';
import OrganizationMultiSelectFilter, {
  OrganizationMultiColumnFilter,
} from 'components/tables/filters/OrganizationMultiSelectFilter';
import { hiddenSearchableColumn as hiddenSearchable } from 'components/tables/utils/hidden-searchable-column';
import { Fragment, useMemo, useState } from 'react';
import { getResponsiveActionsColumnWidth, useResponsiveDesign } from 'hooks/use-responsive-design';

/**
 * Table which displays organizations.
 */
export default function SystemExportTable() {
  const { getAction } = useApi();
  const { tableActionMode } = useResponsiveDesign();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [refreshToken, { increment: incRefreshToken }] = useCounter(0);
  const [downloadModalOpened, { open: openDownloadModal, close: closeDownloadModal }] = useDisclosure(false);
  const [downloadFileId, setDownloadFileId] = useState<string | null>(null);

  const columns = useMemo<ColDef[]>(
    () => [
      // ANCHOR Visible Columns
      {
        field: 'systemExportId',
        headerName: 'ID',
        sortable: true,
        unSortIcon: true,
        width: 76,
        minWidth: 76,
        maxWidth: 76,
        resizable: false,
        cellRenderer: SystemExportIdColumn,
      },
      {
        field: 'organization.organizationName',
        headerName: 'Organizácia',
        minWidth: 200,
        sortable: true,
        filter: true,
        floatingFilter: true,
        floatingFilterComponent: OrganizationMultiSelectFilter,
        unSortIcon: true,
        cellRenderer: OrganizationNameColumn,
      },
      {
        field: 'createdAt',
        headerName: 'Čas exportu',
        filter: DateColumnFilter,
        floatingFilter: true,
        floatingFilterComponent: DateFilter.configure({ filterKey: 'createdAt' }),
        sortable: true,
        unSortIcon: true,
        minWidth: 175,
        cellRenderer: CreatedAtColumn,
      },
      {
        field: 'author.fullName',
        headerName: 'Autor',
        sortable: true,
        unSortIcon: true,
        minWidth: 200,
        cellRenderer: AuthorNameColumn,
      },
      {
        field: 'fileName',
        headerName: 'Súbor',
        sortable: true,
        unSortIcon: true,
        minWidth: 300,
        cellRenderer: FileNameColumn,
      },
      {
        field: 'fileSize',
        headerName: 'Veľkosť súboru',
        sortable: true,
        unSortIcon: true,
        minWidth: 100,
        cellRenderer: FileSizeColumn,
      },

      // ANCHOR Hidden Columns
      {
        field: 'organization.organizationId',
        headerName: 'ID organizácie',
        ...hiddenSearchable,
        filter: OrganizationMultiColumnFilter,
      },

      // ANCHOR Actions
      {
        field: '_actions',
        headerName: '',
        pinned: 'right',
        width: getResponsiveActionsColumnWidth(194, tableActionMode),
        minWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        maxWidth: getResponsiveActionsColumnWidth(194, tableActionMode),
        lockPosition: 'right',
        cellRenderer: ActionsColumn,
      },
    ],
    [tableActionMode]
  );

  return (
    <Fragment>
      <DataTable
        tableId="system-exports"
        title="Systémový export"
        columns={columns}
        action={getAction('SystemExportList')}
        hideToggleDiscarded
        addButtonText="Nový export"
        addButtonIcon={<IconDatabaseExport />}
        onAddButtonClick={openModal}
        dataRefreshToken={refreshToken}
      />
      <SystemExportModal
        opened={modalOpened}
        onClose={closeModal}
        onExport={(_, fileId) => {
          incRefreshToken();
          setDownloadFileId(fileId);
          openDownloadModal();
        }}
      />
      <FileDownloadLinkModal fileId={downloadFileId} opened={downloadModalOpened} onClose={closeDownloadModal} />
    </Fragment>
  );
}
